/** Pixelate — block-mean downsample then nearest-neighbor upsample.
 *
 *  Two-pass implementation: walk the image in N×N blocks, compute
 *  the mean RGBA of each block, then fill every pixel in that block
 *  with the mean. Output is identical in dimensions to source —
 *  it's only the visual "resolution" that's reduced.
 *
 *  Block-mean (rather than top-left or center sample) preserves the
 *  source image's average brightness over the block, which matches
 *  user expectation when "pixelating" — large blocks shouldn't
 *  shift the overall exposure. */

export interface PixelateValue {
  /** Block edge length in pixels (1..50). 1 = identity. */
  blockSize: number;
}

export const identityPixelateValue = (): PixelateValue => ({ blockSize: 1 });

export function applyPixelatePixels(source: ImageData, value: PixelateValue): ImageData {
  const w = source.width;
  const h = source.height;
  const bs = Math.max(1, Math.min(200, Math.floor(value.blockSize)));
  if (bs === 1) {
    return new ImageData(new Uint8ClampedArray(source.data), w, h);
  }
  const src = source.data;
  const out = new Uint8ClampedArray(src.length);
  for (let by = 0; by < h; by += bs) {
    const yEnd = Math.min(h, by + bs);
    for (let bx = 0; bx < w; bx += bs) {
      const xEnd = Math.min(w, bx + bs);
      // Compute block mean.
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let sumA = 0;
      let count = 0;
      for (let y = by; y < yEnd; y++) {
        const rowOff = y * w;
        for (let x = bx; x < xEnd; x++) {
          const i = (rowOff + x) * 4;
          sumR += src[i];
          sumG += src[i + 1];
          sumB += src[i + 2];
          sumA += src[i + 3];
          count++;
        }
      }
      const r = sumR / count;
      const g = sumG / count;
      const b = sumB / count;
      const a = sumA / count;
      // Fill block.
      for (let y = by; y < yEnd; y++) {
        const rowOff = y * w;
        for (let x = bx; x < xEnd; x++) {
          const i = (rowOff + x) * 4;
          out[i] = r;
          out[i + 1] = g;
          out[i + 2] = b;
          out[i + 3] = a;
        }
      }
    }
  }
  return new ImageData(out, w, h);
}

export function applyPixelateToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  value: PixelateValue,
): void {
  const tCtx = target.getContext('2d');
  if (!tCtx) return;
  if (target.width !== source.width || target.height !== source.height) {
    target.width = source.width;
    target.height = source.height;
  }
  tCtx.putImageData(applyPixelatePixels(source, value), 0, 0);
}
